/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { formatLocalizedNumber } from '@/shared/utils/number-format';

/**
 * Formats raw keystrokes as a localized two-decimal number, treating every
 * typed digit as a hundredth (`150000` → `1.500,00`). Digits beyond the 12th
 * are dropped.
 *
 * @param {string} raw - The raw input string from the change event.
 * @returns {string | undefined} The formatted number, or `undefined` when no digit was typed.
 */
const formatCents = (raw: string): string | undefined => {
  const digits = raw.replace(/\D+/g, '').slice(0, 12);
  if (digits === '') return undefined;
  return formatLocalizedNumber(Number(digits) / 100);
};

/**
 * Formats raw keystrokes of a filter-panel money field as localized currency,
 * treating every typed digit as cents (`150000` → `R$ 1.500,00`). Returns
 * `undefined` when no digit remains so the field clears back to its placeholder.
 *
 * @param {string} raw - The raw input string from the change event.
 * @returns {string | undefined} The masked value, or `undefined` when empty.
 */
export const maskMoney = (raw: string): string | undefined => {
  const formatted = formatCents(raw);
  return formatted === undefined ? undefined : `R$ ${formatted}`;
};

/**
 * Formats raw keystrokes of a filter-panel area field, treating every typed
 * digit as hundredths (`4400` → `44,00 m²`), matching the two decimals the
 * property list already shows. Returns `undefined` when no digit remains.
 *
 * @param {string} raw - The raw input string from the change event.
 * @returns {string | undefined} The masked value, or `undefined` when empty.
 */
export const maskArea = (raw: string): string | undefined => {
  const formatted = formatCents(raw);
  return formatted === undefined ? undefined : `${formatted} m²`;
};
